import { useState, useEffect } from 'react';
import { apiJsonFetch } from '../utils/api';
import { 
  Plus, 
  Download,
  Search,
  ChevronRight,
  CheckCircle,
  Clock,
  Truck,
  XCircle,
  X,
  AlertCircle
} from 'lucide-react';

interface Supplier {
  id: string;
  supplierCode: string;
  supplierName: string;
  contactName?: string;
  email?: string;
  phone?: string;
}

interface PurchaseOrder {
  id: string;
  poNumber: string;
  orderDate: string;
  expectedDeliveryDate?: string;
  status: 'draft' | 'ordered' | 'partial_received' | 'received' | 'cancelled';
  totalAmount: string;
  supplier?: Supplier;
}

const statusLabels = {
  draft: { label: '下書き', color: 'bg-gray-100 text-gray-800', icon: Clock },
  ordered: { label: '発注済み', color: 'bg-blue-100 text-blue-800', icon: Truck },
  partial_received: { label: '一部入荷', color: 'bg-yellow-100 text-yellow-800', icon: Truck },
  received: { label: '入荷完了', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  cancelled: { label: 'キャンセル', color: 'bg-red-100 text-red-800', icon: XCircle },
};

export default function PurchaseOrdersPage() {
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewOrderModal, setShowNewOrderModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, [selectedStatus]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = selectedStatus !== 'all' ? `?status=${selectedStatus}` : '';
      const [poRes, suppliersRes] = await Promise.all([
        apiJsonFetch(`/api/purchase-orders${params}`),
        apiJsonFetch('/api/purchase-orders/suppliers'),
      ]);
      if (poRes.ok) {
        setPurchaseOrders(poRes.data.purchaseOrders || []);
      }
      if (suppliersRes.ok) {
        setSuppliers(suppliersRes.data.suppliers || []);
      }
    } catch (error) {
      console.error('データ取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  // CSVエクスポート
  const handleExportCSV = () => {
    if (purchaseOrders.length === 0) return;

    const headers = ['発注番号', '仕入先', '発注日', '納品予定日', '金額', 'ステータス'];
    const rows = purchaseOrders.map(po => [
      po.poNumber,
      po.supplier?.supplierName || '',
      new Date(po.orderDate).toLocaleDateString('ja-JP'),
      po.expectedDeliveryDate ? new Date(po.expectedDeliveryDate).toLocaleDateString('ja-JP') : '',
      Number(po.totalAmount).toLocaleString(),
      statusLabels[po.status]?.label || po.status,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `purchase_orders_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleConfirmOrder = async (poId: string) => {
    try {
      await apiJsonFetch(`/api/purchase-orders/${poId}/confirm`, {
        method: 'POST',
      });
      fetchData();
    } catch (error) {
      console.error('発注確定エラー:', error);
    }
  };

  const [selectedPO, setSelectedPO] = useState<string | null>(null);

  const filteredOrders = purchaseOrders.filter(po => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      po.poNumber.toLowerCase().includes(query) ||
      po.supplier?.supplierName.toLowerCase().includes(query)
    );
  });

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">発注管理</h1>
          <p className="text-sm text-gray-500 mt-1">仕入先への発注・入荷管理</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowNewOrderModal(true)}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-5 w-5" />
            新規発注
          </button>
          <button
            onClick={handleExportCSV}
            className="inline-flex items-center gap-2 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Download className="h-5 w-5" />
            CSVエクスポート
          </button>
        </div>
      </div>

      {/* フィルター */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4">
          {/* 検索 */}
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="発注番号・仕入先名で検索..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* ステータスフィルター */}
          <div className="flex gap-2">
            {['all', 'draft', 'ordered', 'partial_received', 'received'].map((status) => (
              <button
                key={status}
                onClick={() => setSelectedStatus(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedStatus === status
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {status === 'all' ? 'すべて' : statusLabels[status as keyof typeof statusLabels]?.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 発注一覧 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">読み込み中...</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500">発注データがありません</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  発注番号
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  仕入先
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  発注日
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  納品予定日
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  金額
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ステータス
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOrders.map((po) => {
                const statusInfo = statusLabels[po.status];
                const StatusIcon = statusInfo?.icon || Clock;
                return (
                  <tr key={po.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-medium text-gray-900">{po.poNumber}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-gray-900">{po.supplier?.supplierName || '-'}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                      {new Date(po.orderDate).toLocaleDateString('ja-JP')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                      {po.expectedDeliveryDate
                        ? new Date(po.expectedDeliveryDate).toLocaleDateString('ja-JP')
                        : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                      ¥{Number(po.totalAmount).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo?.color}`}>
                        <StatusIcon className="h-3.5 w-3.5" />
                        {statusInfo?.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        {po.status === 'draft' && (
                          <button
                            onClick={() => handleConfirmOrder(po.id)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            発注確定
                          </button>
                        )}
                        <button
                          onClick={() => setSelectedPO(po.id)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
                        >
                          <ChevronRight className="h-4 w-4" />
                          詳細
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* 新規発注モーダル */}
      {showNewOrderModal && (
        <NewOrderModal
          suppliers={suppliers}
          onClose={() => setShowNewOrderModal(false)}
          onSuccess={() => {
            setShowNewOrderModal(false);
            fetchData();
          }}
        />
      )}

      {/* 発注詳細モーダル */}
      {selectedPO && (
        <PODetailModal
          poId={selectedPO}
          onClose={() => setSelectedPO(null)}
          onUpdate={fetchData}
        />
      )}
    </div>
  );
}

// 発注詳細モーダル
function PODetailModal({ poId, onClose, onUpdate }: { poId: string; onClose: () => void; onUpdate: () => void }) {
  const [po, setPO] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [receivingQty, setReceivingQty] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchDetail();
  }, [poId]);

  async function fetchDetail() {
    try {
      const res = await apiJsonFetch(`/api/purchase-orders/${poId}`);
      if (res.ok) {
        const poData = res.data.purchaseOrder;
        setPO(poData);
        const itemList = poData?.items || [];
        setItems(itemList);
        // 初期値設定
        const initial: Record<string, number> = {};
        itemList.forEach((item: any) => {
          initial[item.id] = 0;
        });
        setReceivingQty(initial);
      }
    } catch (error) {
      console.error('詳細取得エラー:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleReceive() {
    const hasQty = Object.values(receivingQty).some(q => q > 0);
    if (!hasQty) {
      alert('入荷数量を入力してください');
      return;
    }

    setSaving(true);
    try {
      const receivingItems = items
        .filter(item => receivingQty[item.id] > 0)
        .map(item => ({
          variantId: item.variantId,
          receivedQuantity: receivingQty[item.id],
        }));

      const res = await apiJsonFetch(`/api/purchase-orders/${poId}/receive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: receivingItems }),
      });

      if (!res.ok) throw new Error(res.data?.error || 'エラーが発生しました');
      
      alert('入荷処理が完了しました');
      onUpdate();
      fetchDetail();
    } catch (error) {
      console.error('入荷処理エラー:', error);
      alert(error instanceof Error ? error.message : '入荷処理に失敗しました');
    } finally {
      setSaving(false);
    }
  }

  const statusInfo = po ? statusLabels[po.status as keyof typeof statusLabels] : null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white rounded-xl shadow-xl">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">発注詳細</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="h-5 w-5" />
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-500 mt-2">読み込み中...</p>
            </div>
          ) : po ? (
            <div className="space-y-6">
              {/* ヘッダー情報 */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">発注番号</p>
                  <p className="font-medium text-gray-900">{po.poNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">ステータス</p>
                  {statusInfo && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                      {statusInfo.label}
                    </span>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-500">仕入先</p>
                  <p className="font-medium text-gray-900">{po.supplier?.supplierName || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">発注日</p>
                  <p className="text-gray-900">{new Date(po.orderDate).toLocaleDateString('ja-JP')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">納品予定日</p>
                  <p className="text-gray-900">{po.expectedDeliveryDate ? new Date(po.expectedDeliveryDate).toLocaleDateString('ja-JP') : '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">合計金額</p>
                  <p className="font-medium text-gray-900">¥{Number(po.totalAmount).toLocaleString()}</p>
                </div>
              </div>

              {po.memo && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">メモ</p>
                  <p className="text-gray-900">{po.memo}</p>
                </div>
              )}

              {/* 明細 */}
              <div>
                <h3 className="font-medium text-gray-900 mb-3">発注明細</h3>
                {items.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">明細がありません</p>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">商品</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">単価</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">発注数</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">入荷済</th>
                        {(po.status === 'ordered' || po.status === 'partial_received') && (
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">今回入荷</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {items.map((item) => (
                        <tr key={item.id}>
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-gray-900">{item.variant?.variantCode || '-'}</p>
                            <p className="text-xs text-gray-500">{item.variant?.master?.productName || '-'}</p>
                          </td>
                          <td className="px-4 py-3 text-right text-sm text-gray-900">
                            ¥{Number(item.unitPrice).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                            {item.quantity}
                          </td>
                          <td className="px-4 py-3 text-right text-sm text-gray-600">
                            {item.receivedQuantity || 0}
                          </td>
                          {(po.status === 'ordered' || po.status === 'partial_received') && (
                            <td className="px-4 py-3 text-right">
                              <input
                                type="number"
                                min="0"
                                max={item.quantity - (item.receivedQuantity || 0)}
                                value={receivingQty[item.id] || 0}
                                onChange={(e) => setReceivingQty({
                                  ...receivingQty,
                                  [item.id]: parseInt(e.target.value) || 0
                                })}
                                className="w-20 px-2 py-1 border border-gray-300 rounded text-right text-sm"
                              />
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* アクション */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                {(po.status === 'ordered' || po.status === 'partial_received') && items.length > 0 && (
                  <button
                    onClick={handleReceive}
                    disabled={saving}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {saving ? '処理中...' : '入荷処理'}
                  </button>
                )}
                <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                  閉じる
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">発注情報を取得できませんでした</div>
          )}
        </div>
      </div>
    </div>
  );
}

// 新規発注モーダル
function NewOrderModal({ suppliers, onClose, onSuccess }: { suppliers: Supplier[]; onClose: () => void; onSuccess: () => void }) {
  const [formData, setFormData] = useState({
    supplierId: '',
    orderDate: new Date().toISOString().split('T')[0],
    expectedDeliveryDate: '',
    memo: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!formData.supplierId) {
      setError('仕入先を選択してください');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await apiJsonFetch('/api/purchase-orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          items: [], // 明細は後から追加
        }),
      });

      if (!res.ok) throw new Error(res.data?.error || 'エラーが発生しました');
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-lg bg-white rounded-xl shadow-xl">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">新規発注</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="h-5 w-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">仕入先 *</label>
              {suppliers.length === 0 ? (
                <p className="text-sm text-gray-500">仕入先が登録されていません。先に仕入先を登録してください。</p>
              ) : (
                <select
                  value={formData.supplierId}
                  onChange={(e) => setFormData({ ...formData, supplierId: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">選択してください</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>{s.supplierName}</option>
                  ))}
                </select>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">発注日 *</label>
                <input
                  type="date"
                  value={formData.orderDate}
                  onChange={(e) => setFormData({ ...formData, orderDate: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">納品予定日</label>
                <input
                  type="date"
                  value={formData.expectedDeliveryDate}
                  onChange={(e) => setFormData({ ...formData, expectedDeliveryDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">メモ</label>
              <textarea
                value={formData.memo}
                onChange={(e) => setFormData({ ...formData, memo: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
            </div>

            <p className="text-sm text-gray-500">
              ※ 発注明細は作成後に追加できます
            </p>

            <div className="flex justify-end gap-3 pt-4">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                キャンセル
              </button>
              <button type="submit" disabled={loading || suppliers.length === 0} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {loading ? '作成中...' : '下書き作成'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

